import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchApi } from '../lib/api';
import CSVUploadModal from '../components/company/CSVUploadModal';
import IdeaDetailDrawer from '../components/company/IdeaDetailDrawer';

const STAGE_LABELS = { new: 'New', in_review: 'In Review', on_hold: 'On Hold', passed: 'Passed', licensing: 'Licensing' };

export default function IdeaPipeline() {
  const [uploads, setUploads] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedUploadId, setSelectedUploadId] = useState(null);
  const [filterStage, setFilterStage] = useState('');
  const [filterRec, setFilterRec] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('wishlist_score');
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const pollRef = useRef(null);

  const loadUploads = useCallback(async () => {
    try {
      const res = await fetchApi('/api/company/ideas/uploads');
      setUploads(res.uploads || []);
      return res.uploads || [];
    } catch { return []; }
  }, []);

  const loadIdeas = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedUploadId) params.set('upload_id', selectedUploadId);
      if (filterStage) params.set('stage', filterStage);
      if (filterRec) params.set('recommendation', filterRec);
      if (search) params.set('search', search);
      if (sortBy) params.set('sort', sortBy);
      params.set('order', 'desc');
      const res = await fetchApi(`/api/company/ideas?${params}`);
      setIdeas(res.ideas || []);
      setTotal(res.total || 0);
    } catch { setIdeas([]); }
  }, [selectedUploadId, filterStage, filterRec, search, sortBy]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadUploads(), loadIdeas()]);
    setLoading(false);
  }, [loadUploads, loadIdeas]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll while processing
  useEffect(() => {
    const hasProcessing = uploads.some(u => u.status === 'processing');
    if (hasProcessing) {
      pollRef.current = setInterval(async () => {
        const fresh = await loadUploads();
        if (!fresh.some(u => u.status === 'processing')) {
          clearInterval(pollRef.current);
          loadIdeas();
        }
      }, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [uploads, loadUploads, loadIdeas]);

  const handleDeleteUpload = async (uploadId) => {
    if (!window.confirm('Delete this upload and all its ideas?')) return;
    try {
      await fetchApi(`/api/company/ideas/uploads/${uploadId}`, { method: 'DELETE' });
      if (selectedUploadId === uploadId) setSelectedUploadId(null);
      loadAll();
    } catch (err) { alert('Delete failed: ' + err.message); }
  };

  const handleStageChange = async (ideaId, stage) => {
    try {
      await fetchApi(`/api/company/ideas/${ideaId}`, { method: 'PUT', body: JSON.stringify({ pipeline_stage: stage }) });
      loadIdeas();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  // Stats
  const scored = ideas.filter(i => i.ai_status === 'complete');
  const stats = [
    { label: 'Total Ideas', value: total, color: '#0F172A' },
    { label: 'AI: Pursue', value: scored.filter(i => i.ai_recommendation === 'pursue').length, color: '#10B981' },
    { label: 'AI: Hold', value: scored.filter(i => i.ai_recommendation === 'hold').length, color: '#c9a84c' },
    { label: 'AI: Pass', value: scored.filter(i => i.ai_recommendation === 'pass').length, color: '#64748B' },
    { label: 'Avg Score', value: scored.length ? Math.round(scored.reduce((s, i) => s + (i.wishlist_score || 0), 0) / scored.length) : '—', color: '#4F46E5' },
  ];

  const scoreBadge = (s) => s >= 80 ? 'bg-emerald-100 text-emerald-700' : s >= 60 ? 'bg-amber-100 text-amber-700' : s >= 40 ? 'bg-gray-100 text-gray-600' : 'bg-gray-50 text-gray-400';
  const recBadge = (r) => r === 'pursue' ? 'bg-emerald-100 text-emerald-700' : r === 'hold' ? 'bg-amber-100 text-amber-700' : r === 'pass' ? 'bg-gray-100 text-gray-500' : 'bg-gray-50 text-gray-300';
  const recLabel = (r) => r === 'pursue' ? '✓ Pursue' : r === 'hold' ? '⏸ Hold' : r === 'pass' ? '✗ Pass' : 'Pending';

  if (loading) {
    return <div className="flex items-center justify-center py-20"><p className="text-sm text-gray-400">Loading idea pipeline...</p></div>;
  }

  // Empty state
  if (!uploads.length && !ideas.length) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-[28px] text-vault-navy">Idea Pipeline</h1>
            <p className="text-sm text-gray-400 mt-1">AI-scored ideas from your uploads + inventor matches</p>
          </div>
        </div>
        <div className="glass p-10 text-center max-w-lg mx-auto">
          <div className="text-5xl mb-4">📥</div>
          <h2 className="font-heading text-xl text-vault-navy mb-3">No ideas uploaded yet</h2>
          <p className="text-sm text-gray-400 leading-relaxed mb-6">
            Upload a CSV of your existing idea pipeline and our AI will score each idea against your wish list, flag duplicates, and surface the best fits.
          </p>
          <button
            onClick={() => setShowUpload(true)}
            className="px-6 py-3 rounded-btn text-sm font-semibold text-white shadow-btn"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            Upload Your First CSV
          </button>
        </div>
        {showUpload && <CSVUploadModal onClose={() => setShowUpload(false)} onUploaded={() => loadAll()} />}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-heading text-[28px] text-vault-navy">Idea Pipeline</h1>
          <p className="text-sm text-gray-400 mt-1">AI-scored ideas from your uploads + inventor matches</p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="px-5 py-2.5 rounded-btn text-sm font-semibold text-white shadow-btn"
          style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
        >
          Upload CSV
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {stats.map(s => (
          <div key={s.label} className="glass p-4">
            <div className="text-[11px] font-semibold text-gray-400 mb-0.5">{s.label}</div>
            <div className="font-heading text-2xl" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-5">
        {/* Uploads sidebar */}
        <div className="w-[200px] shrink-0 hidden lg:block">
          <h3 className="text-[11px] font-semibold text-gray-400 uppercase mb-2">Your Uploads</h3>
          <button
            onClick={() => setSelectedUploadId(null)}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 transition-colors ${
              !selectedUploadId ? 'bg-amber-50 text-vault-gold font-semibold' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            All Ideas
          </button>
          {uploads.map(u => (
            <div key={u.id} className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm mb-0.5 cursor-pointer transition-colors ${
              selectedUploadId === u.id ? 'bg-amber-50 text-vault-gold' : 'text-gray-500 hover:bg-gray-50'
            }`}>
              <button onClick={() => setSelectedUploadId(u.id)} className="flex-1 text-left min-w-0">
                <div className="truncate text-xs font-medium">{u.file_name}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[10px] text-gray-400">{u.row_count} rows</span>
                  {u.status === 'processing' && <span className="text-[10px] text-amber-500 animate-pulse">Processing...</span>}
                  {u.status === 'complete' && <span className="text-[10px] text-emerald-500">Complete</span>}
                  {u.status === 'error' && <span className="text-[10px] text-red-500">Error</span>}
                </div>
              </button>
              {u.status === 'complete' && (
                <button onClick={(e) => { e.stopPropagation(); handleDeleteUpload(u.id); }} className="text-gray-300 hover:text-red-400 text-xs shrink-0">×</button>
              )}
            </div>
          ))}
        </div>

        {/* Ideas table */}
        <div className="flex-1 min-w-0">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <input
              className="flex-1 min-w-[180px] px-3 py-2 rounded-input border-2 border-vault-border text-sm text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none"
              placeholder="Search title or inventor..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select className="px-3 py-2 rounded-input border-2 border-vault-border text-sm text-gray-500 bg-white" value={filterStage} onChange={e => setFilterStage(e.target.value)}>
              <option value="">All Stages</option>
              {Object.entries(STAGE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select className="px-3 py-2 rounded-input border-2 border-vault-border text-sm text-gray-500 bg-white" value={filterRec} onChange={e => setFilterRec(e.target.value)}>
              <option value="">All Recommendations</option>
              <option value="pursue">Pursue</option>
              <option value="hold">Hold</option>
              <option value="pass">Pass</option>
            </select>
            <select className="px-3 py-2 rounded-input border-2 border-vault-border text-sm text-gray-500 bg-white" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="wishlist_score">Score</option>
              <option value="created_at">Upload Date</option>
              <option value="title">Title</option>
            </select>
          </div>

          {/* Table */}
          <div className="glass overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-vault-border">
                  {['Title', 'Inventor', 'Category', 'Score', 'Recommendation', 'Stage', ''].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-vault-border">
                {ideas.map(idea => (
                  <tr key={idea.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <button onClick={() => setSelectedIdea(idea)} className="text-sm font-medium text-vault-navy hover:text-vault-gold text-left truncate max-w-[200px] block">
                        {idea.title}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-500">{idea.inventor_name || '—'}</div>
                      {idea.inventor_email && <div className="text-[10px] text-gray-300">{idea.inventor_email}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {(idea.ai_category || idea.category) && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{idea.ai_category || idea.category}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {idea.ai_status === 'pending' && <span className="text-xs text-amber-500 animate-pulse">Scoring...</span>}
                      {idea.ai_status === 'error' && <span className="text-xs text-red-500">Error</span>}
                      {idea.ai_status === 'complete' && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${scoreBadge(idea.wishlist_score)}`}>
                          {idea.wishlist_score}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${recBadge(idea.ai_recommendation)}`}>
                        {recLabel(idea.ai_recommendation)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        className="text-xs border border-vault-border rounded px-1.5 py-1 bg-white"
                        value={idea.pipeline_stage}
                        onChange={e => handleStageChange(idea.id, e.target.value)}
                      >
                        {Object.entries(STAGE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelectedIdea(idea)} className="text-xs text-vault-gold font-semibold hover:underline">View</button>
                    </td>
                  </tr>
                ))}
                {!ideas.length && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">No ideas match your filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showUpload && <CSVUploadModal onClose={() => setShowUpload(false)} onUploaded={() => loadAll()} />}
      {selectedIdea && <IdeaDetailDrawer idea={selectedIdea} onClose={() => setSelectedIdea(null)} onUpdate={() => { loadIdeas(); }} />}
    </div>
  );
}
